// src/utils/deeplink.js
export function detectPlatform(ua) {
  const U = (ua || navigator.userAgent || '').toLowerCase();
  return {
    isAndroid: /android/.test(U),
    isIOS: /(iphone|ipad|ipod|ios)/.test(U),
  };
}

export function withParams(path, extra = {}) {
  const url = new URL(path, 'https://x'); // dummy base
  const q = new URLSearchParams(url.search);
  for (const [k, v] of Object.entries(extra)) {
    if (v !== undefined && v !== null) q.set(k, String(v));
  }
  return `${url.pathname}${q.toString() ? `?${q}` : ''}${url.hash || ''}`;
}

function hasPiHost(host) {
  return /\.pi$/i.test(host); // true for 'yourapp.pi'
}

export function buildPiLinks({
  host,
  path = '/',
  query = {},
  androidPackage = 'pi.browser',
}) {
  const p = path.startsWith('/') ? path : `/${path}`;
  const qp = withParams(p, query);

  // iOS launch attempt always uses the pi:// scheme (works when Pi Browser handles it).
  // For Android:
  //  - if you DO have a .pi domain -> use scheme=pi
  //  - if you're using a .com       -> use scheme=https so Android opens that https URL in Pi Browser
  const intentScheme = hasPiHost(host) ? 'pi' : 'https';

  return {
    scheme: `pi://${host}${qp}`,
    intent: `intent://${host}${qp}#Intent;scheme=${intentScheme};package=${androidPackage};end`,
    play: 'https://play.google.com/store/apps/details?id=pi.browser',
    appstore: 'https://apps.apple.com/app/id1560911608',
  };
}

// iOS: cancel fallback if we actually switch to Pi Browser (no App Store yank-back)
function openIOSNoBounce(schemeUrl, appStoreUrl) {
  const fallback = setTimeout(() => {
    if (!document.hidden) window.location.href = appStoreUrl;
  }, 1200);

  const cancel = () => { clearTimeout(fallback); cleanup(); };
  const onVis = () => { if (document.visibilityState === 'hidden') cancel(); };

  function cleanup() {
    document.removeEventListener('visibilitychange', onVis, true);
    window.removeEventListener('pagehide', cancel, true);
    window.removeEventListener('blur', cancel, true);
  }

  document.addEventListener('visibilitychange', onVis, true);
  window.addEventListener('pagehide', cancel, true);
  window.addEventListener('blur', cancel, true);

  window.location.href = schemeUrl;

  // safety: stop listening after a bit
  setTimeout(cleanup, 4000);
}

export function smartOpen({ host, path = '/', query = {}, onNoMobile } = {}) {
  const { isAndroid, isIOS } = detectPlatform();
  const links = buildPiLinks({ host, path, query });

  if (isAndroid) {
    // Try scheme first (works if Pi Browser registered pi:// and host is .pi)
    window.location.href = links.scheme;
    // Then force open in Pi Browser via intent.
    setTimeout(() => { window.location.href = links.intent; }, 350);
    // If still not handled, go to Play Store.
    setTimeout(() => { window.location.href = links.play; }, 1300);
  } else if (isIOS) {
    // iOS: launch via pi:// and ONLY fall back to App Store if we didn't switch apps.
    openIOSNoBounce(links.scheme, links.appstore);
  } else {
    onNoMobile?.(links);
  }
  return links;
}
